import React from "react";
import {
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";

// ── Types ──────────────────────────────────────────────────────────

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

// ── Create Brand Dialog ────────────────────────────────────────────

interface CreateBrandDialogProps {
  open: boolean;
  onClose: () => void;
  brandName: string;
  onBrandNameChange: (value: string) => void;
  onSubmit: () => void;
  creating: boolean;
}

export function CreateBrandDialog({
  open,
  onClose,
  brandName,
  onBrandNameChange,
  onSubmit,
  creating,
}: CreateBrandDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Crear nueva marca</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          size="small"
          fullWidth
          label="Nombre de la marca"
          value={brandName}
          onChange={(e) => onBrandNameChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSubmit}
          disabled={creating || !brandName.trim()}
        >
          {creating ? <CircularProgress size={20} /> : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Add Supplier Dialog ────────────────────────────────────────────

interface AddSupplierDialogProps {
  open: boolean;
  onClose: () => void;
  supplierName: string;
  onSupplierNameChange: (value: string) => void;
  onSubmit: () => void;
  adding: boolean;
}

export function AddSupplierDialog({
  open,
  onClose,
  supplierName,
  onSupplierNameChange,
  onSubmit,
  adding,
}: AddSupplierDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Agregar proveedor</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          size="small"
          fullWidth
          label="Nombre del proveedor"
          value={supplierName}
          onChange={(e) => onSupplierNameChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSubmit}
          disabled={adding || !supplierName.trim()}
        >
          {adding ? <CircularProgress size={20} /> : "Agregar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Merge Dialog ───────────────────────────────────────────────────

interface MergeDialogProps {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  onSourceIdChange: (value: string) => void;
  onSubmit: () => void;
  merging: boolean;
}

export function MergeDialog({
  open,
  onClose,
  sourceId,
  onSourceIdChange,
  onSubmit,
  merging,
}: MergeDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Fusionar marcas</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Los proveedores y productos de la marca origen se moveran a la marca
            destino. La marca origen sera eliminada.
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="ID de la marca origen"
            type="number"
            value={sourceId}
            onChange={(e) => onSourceIdChange(e.target.value)}
            helperText="Ingresa el ID de la marca que deseas absorber"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          color="warning"
          onClick={onSubmit}
          disabled={merging || !sourceId}
        >
          {merging ? <CircularProgress size={20} /> : "Fusionar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Feedback Snackbar ──────────────────────────────────────────────

interface FeedbackSnackbarProps {
  snackbar: SnackbarState;
  onClose: () => void;
}

export function FeedbackSnackbar({
  snackbar,
  onClose,
}: FeedbackSnackbarProps): React.ReactElement {
  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={onClose}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
}
